import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import * as XLSX from "xlsx-js-style";
import { CI_MAIN_COUNT, CI_MAIN_INDICATORS, buildNationalCiRanking } from "./dpatScoreStats";

export function isExportHiddenNode(node) {
  if (!node || node.nodeType !== 1) return false;
  if (node.closest?.("[data-export-hide]")) return true;
  const tag = String(node.tagName || "").toUpperCase();
  if (tag === "BUTTON") return true;
  if (node.getAttribute?.("role") === "button") return true;
  return false;
}

function prepareClonedCharts(clonedDoc) {
  clonedDoc.querySelectorAll(".ant-card-head, .ant-card-head-title, .ant-card-body").forEach((el) => {
    el.style.overflow = "visible";
    el.style.whiteSpace = "normal";
    el.style.textOverflow = "clip";
  });
  clonedDoc.querySelectorAll(".recharts-responsive-container").forEach((el) => {
    const box = el.closest(".dpat-summary-chart-box");
    const measured = Math.max(el.offsetHeight || 0, parseFloat(el.style.height) || 0, box?.offsetHeight || 0);
    const height = box ? Math.max(measured, 200) : Math.max(measured, 360);
    el.style.width = "100%";
    el.style.minHeight = `${height}px`;
    el.style.height = `${height}px`;
    el.style.maxHeight = box ? `${height}px` : "";
    el.style.overflow = box ? "hidden" : "visible";
  });
  clonedDoc.querySelectorAll(".recharts-wrapper").forEach((wrapper) => {
    const box = wrapper.closest(".dpat-summary-chart-box");
    const svg = wrapper.querySelector("svg.recharts-surface");
    if (box) {
      wrapper.style.overflow = "visible";
      if (svg) svg.style.overflow = "visible";
      return;
    }
    const width = Math.max(wrapper.offsetWidth || 0, Number(svg?.getAttribute("width")) || 0, 640);
    const height = Math.max(wrapper.offsetHeight || 0, Number(svg?.getAttribute("height")) || 0, 260);
    wrapper.style.overflow = "visible";
    wrapper.style.width = `${width}px`;
    wrapper.style.height = `${height}px`;
    if (svg) {
      svg.setAttribute("width", String(width));
      svg.setAttribute("height", String(height));
      svg.style.overflow = "visible";
      svg.style.width = `${width}px`;
      svg.style.height = `${height}px`;
    }
  });
}

export async function captureNodeForExport(node, extraOptions = {}) {
  const { onclone: extraOnClone, ...rest } = extraOptions;
  return html2canvas(node, {
    scale: 2,
    backgroundColor: "#ffffff",
    logging: false,
    useCORS: true,
    allowTaint: false,
    ...rest,
    ignoreElements: (el) => isExportHiddenNode(el),
    onclone: (clonedDoc, clonedNode) => {
      prepareClonedCharts(clonedDoc);
      extraOnClone?.(clonedDoc, clonedNode);
    },
  });
}

function waitForLayout() {
  return new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
}

function hideExportNodes(root, extraNodes = []) {
  const hidden = [];
  const hide = (el) => {
    if (!el || hidden.some(([node]) => node === el)) return;
    hidden.push([el, el.style.display]);
    el.style.display = "none";
  };
  root?.querySelectorAll?.("[data-export-hide]")?.forEach(hide);
  extraNodes.forEach(hide);
  return hidden;
}

function restoreHiddenNodes(hidden) {
  hidden.forEach(([el, display]) => {
    el.style.display = display;
  });
}

const MAX_CANVAS_SIDE = 32767;
const MAX_CANVAS_PIXELS = 268435456;

function maxSafeHtml2CanvasScale(node, preferred = 2) {
  const width = Math.max(node.scrollWidth || 0, node.getBoundingClientRect?.().width || 0, 1);
  const height = Math.max(node.scrollHeight || 0, node.getBoundingClientRect?.().height || 0, 1);
  const scale = Math.min(
    preferred,
    MAX_CANVAS_SIDE / width,
    MAX_CANVAS_SIDE / height,
    Math.sqrt(MAX_CANVAS_PIXELS / (width * height)),
  );
  return Math.max(0.5, Math.floor(scale * 10) / 10);
}

function measurePdfLayout(root) {
  if (!root?.getBoundingClientRect) {
    return { breakAfterRatio: null, keepTogether: [] };
  }
  const originTop = root.getBoundingClientRect().top;
  const contentHeight = Math.max(root.scrollHeight, root.getBoundingClientRect().height, 1);
  const toRatio = (el, edge = "top") => {
    const box = el.getBoundingClientRect();
    return ((edge === "bottom" ? box.bottom : box.top) - originTop) / contentHeight;
  };

  const regional = root.querySelector("[data-pdf-break-after]");
  const breakAfterRatio = regional ? toRatio(regional, "bottom") : null;
  const keepTogether = [...root.querySelectorAll("thead tr, tbody tr, [data-pdf-keep]")]
    .filter((row) => !row.closest("[data-export-hide]"))
    .map((row) => ({
      top: toRatio(row, "top"),
      bottom: toRatio(row, "bottom"),
    }))
    .filter((row) => Number.isFinite(row.top) && row.bottom > row.top);

  return { breakAfterRatio, keepTogether };
}

async function captureVisibleNode(node, extraOptions = {}) {
  const originalOverflow = node.style.overflow;
  node.style.overflow = "visible";
  await waitForLayout();
  try {
    const scale = extraOptions.scale ?? maxSafeHtml2CanvasScale(node);
    const layout = measurePdfLayout(node);
    const canvas = await captureNodeForExport(node, { ...extraOptions, scale });
    return { canvas, layout };
  } finally {
    node.style.overflow = originalOverflow;
  }
}

function nextPageCutMm(y, pageHeight, imgHeight, { breakAfterMm = null, rowBands = [] } = {}) {
  const naturalNext = Math.min(y + pageHeight, imgHeight);
  let end = naturalNext;

  if (breakAfterMm != null && breakAfterMm > y + 1 && breakAfterMm < naturalNext - 0.4) {
    end = breakAfterMm;
  }

  let snap = null;
  rowBands.forEach((row) => {
    const splitsRow = row.top < end - 0.25 && row.bottom > end + 0.25;
    const fitsOnNextPage = row.bottom - row.top <= pageHeight - 2;
    const startedAfterPageTop = row.top > y + 1.2;
    if (!splitsRow || !fitsOnNextPage || !startedAfterPageTop) return;
    if (snap == null || row.top < snap) snap = row.top;
  });
  if (snap != null) end = snap;

  if (end <= y + 1) return naturalNext;
  return end;
}

export function appendCanvasToPdf(pdf, canvas, options = {}) {
  if (!canvas || !(canvas.width > 0) || !(canvas.height > 0)) return pdf;

  const { startOnNewPage = false, breakAfterRatio = null, keepTogether = [] } = options;
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const imgWidth = pageWidth;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;
  const imgData = canvas.toDataURL("image/png");
  const breakAfterMm =
    breakAfterRatio != null ? Number(breakAfterRatio) * imgHeight : null;
  const rowBands = (keepTogether || []).map((row) => ({
    top: Number(row.top) * imgHeight,
    bottom: Number(row.bottom) * imgHeight,
  }));

  let y = 0;
  let pageIndex = 0;
  let addPageFirst = startOnNewPage;
  while (y < imgHeight - 0.2) {
    if (addPageFirst || pageIndex > 0) pdf.addPage();
    addPageFirst = false;
    pdf.addImage(imgData, "PNG", 0, -y, imgWidth, imgHeight);

    const end = nextPageCutMm(y, pageHeight, imgHeight, { breakAfterMm, rowBands });
    if (end < y + pageHeight - 0.4) {
      pdf.setFillColor(255, 255, 255);
      pdf.rect(0, end - y, pageWidth, pageHeight - (end - y) + 0.4, "F");
    }
    y = end;
    pageIndex += 1;
  }
  return pdf;
}

export function downloadPdfFromCanvas(canvas, filename, layout = {}) {
  const orientation = layout.orientation === "landscape" ? "landscape" : "portrait";
  const pdf = new jsPDF({ orientation, unit: "mm", format: "a4" });
  appendCanvasToPdf(pdf, canvas, layout);
  pdf.save(filename);
}


export function downloadWordFromHtml(html, filename) {
  const content = `
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="utf-8"><title>DPAT Dashboard</title>
<style>
  body { font-family: Arial, Helvetica, sans-serif; color: #16325a; }
  h1, h2 { color: #16325a; }
  table { border-collapse: collapse; width: 100%; font-size: 11px; }
  th, td { border: 1px solid #d0d7e2; padding: 6px 8px; text-align: left; }
  th { background: #16325a; color: #fff; }
  img { max-width: 650px; }
</style>
</head>
<body>${html}</body></html>`;
  const blob = new Blob(["\ufeff", content], { type: "application/msword" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename.endsWith(".doc") ? filename : `${filename}.doc`;
  link.click();
  URL.revokeObjectURL(url);
}



export function printDashboardAsPdf(node, filename) {
  if (!node) return;
  const previousTitle = document.title;
  const printName = String(filename || "dpat-dashboard").replace(/\.pdf$/i, "");
  document.title = printName;
  node.setAttribute("data-dpat-print-root", "true");
  document.documentElement.classList.add("dpat-dashboard-printing");

  const cleanup = () => {
    document.title = previousTitle;
    node.removeAttribute("data-dpat-print-root");
    document.documentElement.classList.remove("dpat-dashboard-printing");
    window.removeEventListener("afterprint", cleanup);
  };

  window.addEventListener("afterprint", cleanup);
  window.print();
  window.setTimeout(cleanup, 1500);
}

export async function downloadNodeAsPdf(node, filename, options = {}) {
  const hidden = hideExportNodes(node);
  try {
    const { canvas, layout } = await captureVisibleNode(node);
    downloadPdfFromCanvas(canvas, filename, { ...layout, ...options });
  } finally {
    restoreHiddenNodes(hidden);
  }
}

export async function downloadDashboardAsPdf(root, filename) {
  if (!root) return;

  const ciNode = root.querySelector("[data-pdf-part='ci-ranking']");
  const firstHidden = hideExportNodes(root, ciNode ? [ciNode] : []);

  let firstCapture;
  try {
    firstCapture = await captureVisibleNode(root);
  } finally {
    restoreHiddenNodes(firstHidden);
  }

  let ciCapture = null;
  if (ciNode) {
    const ciHidden = hideExportNodes(ciNode);
    try {
      ciCapture = await captureVisibleNode(ciNode);
    } finally {
      restoreHiddenNodes(ciHidden);
    }
  }

  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  appendCanvasToPdf(pdf, firstCapture.canvas, firstCapture.layout);
  if (ciCapture) {
    appendCanvasToPdf(pdf, ciCapture.canvas, {
      ...ciCapture.layout,
      startOnNewPage: true,
    });
  }
  pdf.save(filename);
}

export async function downloadPagedNodeAsPdf(root, filename, options = {}) {
  if (!root) return;

  await waitForLayout();
  const parts = [...root.querySelectorAll("[data-pdf-part]")].filter((el) => {
    if (el.closest("[data-export-hide]")) return false;
    if (el.parentElement?.closest("[data-pdf-part]")) return false;
    const height = Math.max(el.scrollHeight || 0, el.getBoundingClientRect?.().height || 0);
    return height > 8;
  });
  const nodes = parts.length ? parts : [root];
  const orientation = options.orientation === "landscape" ? "landscape" : "portrait";
  const pdf = new jsPDF({ orientation, unit: "mm", format: "a4" });

  let started = false;
  for (const node of nodes) {
    const hidden = hideExportNodes(node);
    try {
      const { canvas, layout } = await captureVisibleNode(node, { scale: options.scale });
      if (!canvas || canvas.width < 4 || canvas.height < 4) continue;
      appendCanvasToPdf(pdf, canvas, {
        ...layout,
        startOnNewPage: started,
      });
      started = true;
    } finally {
      restoreHiddenNodes(hidden);
    }
  }

  if (started) pdf.save(filename);
}

export async function downloadNodeAsWord(node, filename, heading = "") {
  const canvas = await captureNodeForExport(node);
  const img = canvas.toDataURL("image/png");
  downloadWordFromHtml(
    `${heading ? `<h1>${heading}</h1>` : ""}<img src="${img}" width="650" />`,
    filename,
  );
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function pillarCell(actual, max) {
  const a = Number(actual);
  const m = Number(max);
  if (!Number.isFinite(a) || !Number.isFinite(m) || m <= 0) return "—";
  return `${a} / ${m} (${((a / m) * 100).toFixed(1)}%)`;
}

export function buildDistrictRankingHtml(rows, { title, year, showRegion = true, scoreLabel = "Final Outcome" }) {
  const header = `
    <tr>
      <th>Rank</th>
      <th>District</th>
      ${showRegion ? "<th>Region</th>" : ""}
      <th>${escapeHtml(scoreLabel)}</th>
      <th>%</th>
      <th>SDI</th>
      <th>PI</th>
      <th>Classification</th>
      <th>Compliance</th>
    </tr>`;
  const body = (rows || [])
    .map((row) => {
      const overall = row.hasData
        ? `${row.overallScore ?? "—"} / ${row.overallMax ?? "—"}`
        : "No data";
      const percent = row.overallPercent != null ? `${row.overallPercent.toFixed(1)}%` : "—";
      const compliance =
        row.hasData && row.complianceTotal > 0
          ? `${row.complianceFulfilled} / ${row.complianceTotal}`
          : "—";
      return `<tr>
        <td>${escapeHtml(row.rank ?? "—")}</td>
        <td>${escapeHtml(row.districtName)}</td>
        ${showRegion ? `<td>${escapeHtml(row.regionName)}</td>` : ""}
        <td>${escapeHtml(overall)}</td>
        <td>${escapeHtml(percent)}</td>
        <td>${escapeHtml(row.hasData ? pillarCell(row.sdiActual, row.sdiMax) : "—")}</td>
        <td>${escapeHtml(row.hasData ? pillarCell(row.piActual, row.piMax) : "—")}</td>
        <td>${escapeHtml(row.classification || "—")}</td>
        <td>${escapeHtml(compliance)}</td>
      </tr>`;
    })
    .join("");
  return `<h2>${escapeHtml(title || "District ranking")}</h2><p>${escapeHtml(year || "")}</p><table>${header}${body}</table>`;
}

export function buildRegionRankingHtml(rows, year) {
  const header = `<tr><th>Rank</th><th>Region</th><th>Avg Final Outcome %</th><th>Classification</th><th>Districts Assemblies Assessed</th></tr>`;
  const body = (rows || [])
    .map(
      (row) => `<tr>
        <td>${escapeHtml(row.rank)}</td>
        <td>${escapeHtml(row.regionName)}</td>
        <td>${escapeHtml(row.regionAveragePercent?.toFixed?.(1) ?? "—")}%</td>
        <td>${escapeHtml(row.classification || "—")}</td>
        <td>${escapeHtml(`${row.assessedCount} / ${row.districtCount}`)}</td>
      </tr>`,
    )
    .join("");
  return `<h2>National region ranking</h2><p>${escapeHtml(year || "")}</p><table>${header}${body}</table>`;
}

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function buildDistrictRankingRows(rows, { showRegion = true, scoreLabel = "Final Outcome" } = {}) {
  const header = [
    "Rank",
    "District",
    ...(showRegion ? ["Region"] : []),
    scoreLabel,
    "%",
    "SDI",
    "PI",
    "Classification",
    "Compliance",
  ];
  const body = (rows || []).map((row) => {
    const overall = row.hasData
      ? `${row.overallScore ?? "—"} / ${row.overallMax ?? "—"}`
      : "No data";
    const percent = row.overallPercent != null ? `${row.overallPercent.toFixed(1)}%` : "—";
    const compliance =
      row.hasData && row.complianceTotal > 0
        ? `${row.complianceFulfilled} / ${row.complianceTotal}`
        : "—";
    return [
      row.rank ?? "—",
      row.districtName ?? "—",
      ...(showRegion ? [row.regionName ?? "—"] : []),
      overall,
      percent,
      row.hasData ? pillarCell(row.sdiActual, row.sdiMax) : "—",
      row.hasData ? pillarCell(row.piActual, row.piMax) : "—",
      row.classification || "—",
      compliance,
    ];
  });
  return [header, ...body];
}

export function buildRegionRankingRows(rows) {
  const header = [
    "Rank",
    "Region",
    "Avg Final Outcome %",
    "Classification",
    "Districts Assemblies Assessed",
  ];
  const body = (rows || []).map((row) => [
    row.rank ?? "—",
    row.regionName ?? "—",
    row.regionAveragePercent != null ? `${Number(row.regionAveragePercent).toFixed(1)}%` : "—",
    row.classification || "—",
    `${row.assessedCount ?? 0} / ${row.districtCount ?? 0}`,
  ]);
  return [header, ...body];
}

function formatCiExportCell(fulfilled) {
  if (fulfilled === true) return "Fulfilled";
  if (fulfilled === false) return "Not Fulfilled";
  return "—";
}

function ciLookup(list, key) {
  return (list || []).find((item) => item.key === key);
}

export function buildCiRankingRows(rows) {
  const ranked = buildNationalCiRanking(rows);
  const header = [
    "Rank",
    "District",
    "Region",
    "Total CI",
    ...CI_MAIN_INDICATORS.map((indicator) => indicator.code),
  ];
  const body = ranked.map((row) => {
    const total = `${row.complianceFulfilled ?? 0} / ${row.complianceTotal || CI_MAIN_COUNT}`;
    const indicatorCells = CI_MAIN_INDICATORS.map((indicator) =>
      formatCiExportCell(ciLookup(row.ciIndicators, indicator.key)?.fulfilled),
    );
    return [
      row.rank ?? "—",
      row.districtName ?? "—",
      row.regionName ?? "—",
      total,
      ...indicatorCells,
    ];
  });
  return [header, ...body];
}

export function buildCiRankingHtml(rows, year) {
  const ranked = buildNationalCiRanking(rows);
  const header = `
    <tr>
      <th>Rank</th>
      <th>District</th>
      <th>Region</th>
      <th>Total CI</th>
      ${CI_MAIN_INDICATORS.map(
        (indicator) =>
          `<th>${escapeHtml(indicator.code)} — ${escapeHtml(indicator.thematicArea)}</th>`,
      ).join("")}
    </tr>`;
  const body = ranked
    .map((row) => {
      const total = `${row.complianceFulfilled ?? 0} / ${row.complianceTotal || CI_MAIN_COUNT}`;
      const indicatorCells = CI_MAIN_INDICATORS.map((indicator) =>
        formatCiExportCell(ciLookup(row.ciIndicators, indicator.key)?.fulfilled),
      );
      return `<tr>
        <td>${escapeHtml(row.rank ?? "—")}</td>
        <td>${escapeHtml(row.districtName)}</td>
        <td>${escapeHtml(row.regionName)}</td>
        <td>${escapeHtml(total)}</td>
        ${indicatorCells.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}
      </tr>`;
    })
    .join("");
  return `<h2>National Compliance Indicators (CI) Ranking</h2><p>${escapeHtml(year || "")}</p><table>${header}${body}</table>`;
}

export function buildDashboardKpiRows(summary, year) {
  return [
    ["DPAT Dashboard", String(year || "")],
    [],
    ["Metric", "Value"],
    ["Districts Assemblies Assessed", summary?.assessedCount ?? "—"],
    [
      "National average %",
      summary?.nationalAvg != null ? Number(summary.nationalAvg).toFixed(1) : "—",
    ],
    ["SDI average %", summary?.sdiAvg != null ? Number(summary.sdiAvg).toFixed(1) : "—"],
    ["PI average %", summary?.piAvg != null ? Number(summary.piAvg).toFixed(1) : "—"],
    ["CI average %", summary?.ciAvg != null ? Number(summary.ciAvg).toFixed(1) : "—"],
    ["Yet to be assessed", summary?.missingCount ?? "—"],
  ];
}

export function downloadExcelSheets(sheets, filename) {
  const wb = XLSX.utils.book_new();
  (sheets || []).forEach(({ name, rows }) => {
    const ws = XLSX.utils.aoa_to_sheet(rows || []);
    XLSX.utils.book_append_sheet(wb, ws, String(name || "Sheet").slice(0, 31));
  });
  XLSX.writeFile(wb, filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`);
}

export function downloadCsvFromRows(rows, filename) {
  const ws = XLSX.utils.aoa_to_sheet(rows || []);
  const csv = XLSX.utils.sheet_to_csv(ws);
  const blob = new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8;" });
  triggerBlobDownload(blob, filename.endsWith(".csv") ? filename : `${filename}.csv`);
}
